import React from 'react';
import { Theme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import { lighten, styled } from '@mui/material/styles';
import { useTranslation } from '@askporter/config-provider';
import { Typography } from '../Typography';

export interface WebchatMessageProps {
  /**
   * The message text content (optional if children provided)
   */
  text?: string;
  /**
   * Sender type: 'bot' or 'user'
   */
  sender: 'bot' | 'user';
  /**
   * Optional timestamp for the message (display format)
   */
  timestamp?: string;
  /**
   * Optional ISO 8601 time format for datetime attribute (e.g., "12:00:00")
   */
  datetime?: string;
  /**
   * Avatar URL for the sender (optional)
   */
  avatarUrl?: string;
  /**
   * Avatar initials for the sender (optional)
   */
  avatarInitials?: string;
  /**
   * Optional children to render instead of text
   */
  children?: React.ReactNode;
  /**
   * Whether to remove the minimum width of the message bubble
   */
  removeMinWidth?: boolean;

  /**
   * Whether to remove the maximum width of the message bubble
   */
  removeMaxWidth?: boolean;
  /**
   * Custom bot name to display instead of the default "Assistant" label
   */
  botName?: string;
  /**
   * Custom sender label for the header (overrides botName and default user/bot names)
   */
  senderDisplayName?: string;
  /**
   * When true, only the header (and bot avatar) are shown — no message bubble
   */
  hideBubble?: boolean;
  /**
   * When true, removes the border from the message bubble
   */
  hideBorder?: boolean;
}

export const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'sender',
})<{ sender: 'bot' | 'user' }>(({ theme, sender }) => ({
  display: 'flex',
  flexDirection: sender === 'user' ? 'row-reverse' : 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  marginBottom: theme.spacing(1),
  marginInlineStart: theme.spacing(1),
  marginInlineEnd: theme.spacing(1),
}));

const MessageArticle = styled('article', {
  shouldForwardProp: (prop) => prop !== 'sender',
})<{ sender: 'bot' | 'user' }>(({ theme, sender }) => ({
  display: 'flex',
  flexDirection: sender === 'user' ? 'row-reverse' : 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  marginBottom: theme.spacing(1),
  marginInlineStart: theme.spacing(1),
  marginInlineEnd: theme.spacing(1),
}));

const MessageHeader = styled('header')<{ sender: 'bot' | 'user' }>(({ theme, sender }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  justifyContent: sender === 'bot' ? 'space-between' : 'flex-end',
  marginBottom: theme.spacing(0.75),
}));

const SenderName = styled('h3')(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  margin: 0,
}));

const TimestampTime = styled('time')(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.grey[500],
  textAlign: 'end',
}));

const Avatar = styled(Box)(({ theme }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.grey[300],
  flexShrink: 0,
  fontSize: '14px',
  fontWeight: 500,
  color: theme.palette.text.primary,
  overflow: 'hidden',
}));

export const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'sender' && prop !== 'removeMinWidth' && prop !== 'removeMaxWidth' && prop !== 'hideBorder',
})<{ sender: 'bot' | 'user'; removeMinWidth?: boolean; removeMaxWidth?: boolean; hideBorder?: boolean }>(
  ({ theme, sender, removeMinWidth, removeMaxWidth, hideBorder }) => {
    const baseRadius = '8px';
    const borderRadius = removeMaxWidth
      ? 0
      : sender === 'user'
      ? {
          borderStartStartRadius: baseRadius,
          borderStartEndRadius: 0,
          borderEndStartRadius: baseRadius,
          borderEndEndRadius: baseRadius,
        }
      : {
          borderStartStartRadius: 0,
          borderStartEndRadius: baseRadius,
          borderEndStartRadius: baseRadius,
          borderEndEndRadius: baseRadius,
        };
    return {
      ...(removeMaxWidth ? {} : { maxWidth: 480 }),
      minWidth: removeMinWidth ? 'auto' : 250,
      width: removeMinWidth && !removeMaxWidth ? 'fit-content' : undefined,
      padding: removeMaxWidth ? 0 : '8px 12px',
      ...(typeof borderRadius === 'object' ? borderRadius : { borderRadius }),
      border:
        removeMaxWidth || hideBorder
          ? 'none'
          : sender === 'user'
          ? `1px solid ${theme.palette.primary.main}`
          : '1px solid #E9EAEB',
      backgroundColor: removeMaxWidth
        ? 'transparent'
        : sender === 'user'
        ? lighten(theme.palette.primary.main, 0.9)
        : '#F5F5F5',
      color: '#181D27',
      wordBreak: 'break-word',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      overflow: removeMaxWidth ? 'visible' : 'hidden',
    };
  },
);

const MessageParagraph = styled('p')(() => ({
  fontSize: '16px',
  lineHeight: '20px',
  margin: 0,
  whiteSpace: 'pre-wrap',
}));

/**
 * WebchatMessage displays a message bubble with sender information
 * and timestamp. It supports both bot and user messages with different
 * styling based on the role. Can display either text or custom children.
 */
export const WebchatMessage: React.FC<WebchatMessageProps> = ({
  text,
  sender,
  timestamp,
  datetime,
  avatarUrl,
  avatarInitials,
  children,
  removeMinWidth = false,
  removeMaxWidth = false,
  botName,
  senderDisplayName,
  hideBubble = false,
  hideBorder = false,
}) => {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const senderName =
    senderDisplayName ?? (sender === 'bot' ? botName || t('webchat:assistant_name') : t('webchat:user_name'));

  return (
    <MessageArticle
      role="group"
      aria-label={`Message from ${senderName}`}
      sender={sender}
      data-testid={sender === 'bot' ? 'chat-message-assistant' : 'chat-message-user'}
      style={removeMaxWidth ? { overflow: 'visible' } : undefined}
      tabIndex={-1}
    >
      {sender === 'bot' && (
        <Avatar
          sx={{ mt: 1 }}
          aria-hidden="true"
          style={{
            width: '24px',
            height: '24px',
            minWidth: '24px',
            minHeight: '24px',
            maxWidth: '24px',
            maxHeight: '24px',
          }}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={sender === 'bot' ? t('webchat:assistant_avatar') : t('webchat:user_avatar')}
              width={24}
              height={24}
              style={{ width: '24px', height: '24px', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500, fontSize: '14px' }}>
              {avatarInitials || (sender === 'bot' ? 'B' : 'U')}
            </Typography>
          )}
        </Avatar>
      )}
      <Box
        sx={{
          marginInlineStart: sender === 'bot' ? 2 : 0,
          ...(removeMaxWidth
            ? { overflow: 'visible', width: 'fit-content', alignSelf: 'flex-start', minWidth: 0 }
            : removeMinWidth
            ? {
                width: 'fit-content',
                maxWidth: isSmallScreen ? '80%' : '480px',
                alignSelf: 'flex-start',
                minWidth: 0,
                ...(sender === 'user' ? { marginInlineStart: 'auto' } : {}),
              }
            : { maxWidth: isSmallScreen ? '80%' : '480px' }),
        }}
      >
        <MessageHeader sender={sender} style={removeMaxWidth ? { marginInlineEnd: '42px' } : undefined}>
          <SenderName>{senderName}</SenderName>
          {timestamp && <TimestampTime dateTime={datetime || timestamp}>{timestamp}</TimestampTime>}
        </MessageHeader>
        {!hideBubble && (
          <MessageBubble
            sender={sender}
            removeMinWidth={removeMinWidth}
            removeMaxWidth={removeMaxWidth}
            hideBorder={hideBorder}
            sx={{ mt: '6px' }}
          >
            {children ? children : text ? <MessageParagraph>{text}</MessageParagraph> : null}
          </MessageBubble>
        )}
      </Box>
    </MessageArticle>
  );
};
